import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import {
  OfflineHeaderModule,
  DialogV2Service,
  MockTranslationModule,
  SidebarV3Component,
  SnackbarV3Component,
  DialogV2Module,
  SidepanelTypesV3,
} from '@gea/digital-ui-lib';
import { MessageService } from 'primeng/api';
import { UnsavedChangesService } from '../core/guards/unsaved-changes/services/unsaved-changes.service';
import { DxTableService } from '../shared/components/dx-table/services/dx-table.service';
import { ParameterService } from '../shared/services/shared-service/parameter.service';
import { first, Subscription, forkJoin, catchError, of, merge } from 'rxjs';
import { CommonModule } from '@angular/common';
import { GrafcetDataService } from '../shared/services/shared-service/grafcet-data.service';
import { HeaderComponent } from '../shared/components/header/header.component';
import { environment } from '../core/environments/environment';
import { DatabaseService } from './database/components/services/database-service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AppInitService } from '../shared/services/shared-service/app-init.service';
import { AppNoAccess } from '../shared/components/app-no-access/app-no-access';
import { PermissionService } from '../core/services/permission.service';
import { ControllerService } from '../features/general/components/definitions/components/controller/services/controller.service';
import { AreaService } from '../features/general/components/definitions/components/area/services/area.service';

@Component({
  selector: 'app-features',
  imports: [
    CommonModule,
    SidebarV3Component,
    MockTranslationModule,
    OfflineHeaderModule,
    SnackbarV3Component,
    RouterOutlet,
    DialogV2Module,
    HeaderComponent,
    ConfirmDialogModule,
    AppNoAccess,
  ],
  providers: [DialogV2Service, MessageService],
  templateUrl: './features.component.html',
  styleUrl: './features.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeaturesComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public readonly dialogV2Service: DialogV2Service,
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly grafcetDataService: GrafcetDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly permissionService: PermissionService,
    private readonly controllerService: ControllerService,
    private readonly areaService: AreaService,
  ) {}

  @ViewChild('sidebarRoot', { static: false }) sidebarRootRef!: ElementRef;

  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly dxTableService = inject(DxTableService);
  private readonly parameterService = inject(ParameterService);
  public databaseService = inject(DatabaseService);
  private readonly appInitService = inject(AppInitService);

  private readonly subscription = new Subscription();
  public slimView: boolean = false;
  public spanded: boolean = true;
  private hidden: boolean = false;
  private lastViewportWidth: number = 0;
  public navItems: any[] = [];
  public hasAccess: boolean | null = environment.name.includes('cloud') ? null : true;
  public bottomItems: any[] = [];
  public activeSidebarList: 'navigation' | 'bottom' = 'navigation';
  private hasControllers: boolean = false;
  private hasAreas: boolean = false;
  private isConnectedFlag: boolean = false;
  private globalClickListener!: () => void;
  private readonly onBeforeUnload = (event: BeforeUnloadEvent) => this.handleBeforeUnload(event);
  private readonly boundUpdateSidebarIconPosition = () => this.updateSidebarIconPosition();

  public ngOnInit(): void {
    const isCloudEnv = environment.name.includes('cloud');
    this.checkToken(isCloudEnv);
    this.subscription.add(
      this.databaseService.isConnected$.subscribe((isConnected) => {
        this.isConnectedFlag = isConnected ?? false;
        if (isConnected) {
          this.loadControllerAndAreaData(isCloudEnv);
        } else {
          this.hasControllers = false;
          this.hasAreas = false;
          this.buildNavItems(false, isCloudEnv);
        }
      }),
    );

    this.subscription.add(
      merge(this.controllerService.dataChanged$, this.areaService.dataChanged$).subscribe(() => {
        if (this.isConnectedFlag) {
          this.loadControllerAndAreaData(isCloudEnv);
        }
      }),
    );

    this.setupRouterEvents();
  }

  private loadControllerAndAreaData(isCloudEnv: boolean): void {
    forkJoin([
      this.controllerService.getMinimalControllers().pipe(catchError(() => of([]))),
      this.areaService.getMinimalAreas().pipe(catchError(() => of([]))),
    ])
      .pipe(first())
      .subscribe(([controllers, areas]) => {
        this.hasControllers = Array.isArray(controllers) && controllers.length > 0;
        this.hasAreas = Array.isArray(areas) && areas.length > 0;

        this.buildNavItems(this.isConnectedFlag, isCloudEnv);
      });
  }

  private setupRouterEvents(): void {
    setTimeout(() => {
      this.updateSidebarIconPosition();
    }, 300);

    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.activeSidebarList = this.isBottomNavigationRoute(event.urlAfterRedirects) ? 'bottom' : 'navigation';
        }

        if (event instanceof NavigationStart) {
          if (event.url.startsWith('/login') || event.url.startsWith('/change-password')) {
            this.unsavedChangesService.setState(false);
            this.unsavedChangesService.setCancelNavigation('');
            this.grafcetDataService.setGrafcetChanges(false);
            return;
          }

          if (this.unsavedChangesService.getState() || this.grafcetDataService.getGrafcetChanges()) {
            const targetUrl = event.url;
            const currentUrl = this.router.url;
            if (targetUrl !== currentUrl) {
              this.router.navigateByUrl(currentUrl, { replaceUrl: true });
            }
            this.reloadContent();
            this.dialogV2Service.open({
              title: 'You have unsaved changes',
              message: 'Do you want to leave without saving the changes made in this page?',
              yes: 'Exit without saving',
              no: 'Cancel',
              buttonTypeYes: 'primary',
              buttonTypeNo: 'secondary',
              confirmCallback: () => {
                this.unsavedChangesService.setState(false);
                this.grafcetDataService.setGrafcetChanges(false);
                this.dxTableService.resetData();
                this.grafcetDataService.setUpdatedPhaseClass(null);
                this.grafcetDataService.resetPhaseSteps();
                this.grafcetDataService.resetPhaseSOCTransitions();
                this.grafcetDataService.resetPhaseSocTransitionCondition();
                this.grafcetDataService.resetUpdatedStep(true);
                this.grafcetDataService.resetUpdatedSocTransition(true);
                this.grafcetDataService.resetUpdatedPhaseSteps();
                this.grafcetDataService.resetUpdatedPhaseSocTransitions();
                this.grafcetDataService.updatePhaseActivationRevert(true);
                const initPhaseSteps = this.grafcetDataService.getInitPhaseStepData();
                const initPhaseSocTransitions = this.grafcetDataService.getInitPhaseSocTransition();
                const initPhaseSocTransitionCondition = this.grafcetDataService.getInitPhaseSocTransitionCondition();
                const initPhaseValidations = this.grafcetDataService.getInitPhaseValidationData();
                this.grafcetDataService.updatePhaseValidations(initPhaseValidations);
                // This will always be the original data, not modified
                this.grafcetDataService.updatePhaseSocTransitions(initPhaseSocTransitions);
                this.grafcetDataService.updatePhaseStep(initPhaseSteps);
                this.grafcetDataService.updatePhaseSocTransitionCondition(initPhaseSocTransitionCondition);
                this.parameterService.resetData();
                this.router.navigateByUrl(targetUrl);
                this.dialogV2Service.close();
                this.reloadContent();
              },

              rejectCallback: () => {
                this.unsavedChangesService.setCancelNavigation(currentUrl);
                this.dialogV2Service.close();
                this.reloadContent();
              },
              showRejectButton: true,
            });
            this.reloadContent();
          }
        }
      }),
    );
  }

  private buildNavItems(isConnected: boolean, isCloudEnv: boolean): void {
    const isControlModulesDisabled = !isConnected || !this.hasControllers || !this.hasAreas;
    const rawNavItems = [
      {
        expanded: false,
        icon: 'GeneralIcon',
        label: 'General',
        route: 'general/definitions/projects',
        disabled: !isConnected,
        children: [
          { icon: '', label: 'Definitions', route: 'general/definitions/projects', disabled: !isConnected },
          { icon: '', label: 'Settings', route: 'general/settings', disabled: !isConnected },
        ],
      },
      {
        icon: 'ControlModules',
        label: 'Control Modules',
        route: '/control-modules/modules/valve',
        disabled: isControlModulesDisabled,
        children: [
          { icon: '', label: 'Modules', route: '/control-modules/modules/valve', disabled: isControlModulesDisabled },
          { icon: '', label: 'Presets', route: '/control-modules/presets/interlock-texts', disabled: !isConnected },
          { icon: '', label: 'Overview', route: '/control-modules/overview', disabled: !isConnected },
          { icon: '', label: 'IO Check', route: '/control-modules/io-check', disabled: !isConnected },
        ],
      },
      {
        icon: 'Phases',
        label: 'Phases',
        route: '/phases/definitions',
        disabled: !isConnected,
        children: [
          { icon: '', label: 'Definitions', route: '/phases/definitions', disabled: !isConnected },
          { icon: '', label: 'Presets', route: '/phases/presets/alarm-warning-info-texts', disabled: !isConnected },
          { icon: '', label: 'Overview', route: '/phases/overview', disabled: !isConnected },
        ],
      },
      {
        icon: 'EntitiesIcon',
        label: 'Entities',
        route: '/entities/definitions',
        disabled: !isConnected,
        children: [
          { icon: '', label: 'Definitions', route: '/entities/definitions', disabled: !isConnected },
          { icon: '', label: 'Presets', route: '/entities/presets', disabled: !isConnected },
          { icon: '', label: 'Overview', route: '/entities/overview', disabled: !isConnected },
        ],
      },
      {
        icon: 'book-open',
        label: 'Recipe',
        route: '/recipe/definitions',
        disabled: !isConnected,
        children: [
          { icon: '', label: 'Definitions', route: '/recipe/definitions', disabled: !isConnected },
          { icon: '', label: 'Overview', route: '/recipe/overview', disabled: !isConnected },
        ],
      },
      // { icon: 'FDSIcon', label: 'FDS', route: '/fds' }
      { icon: 'download', label: 'Import / Export', route: '/import-export', disabled: !isConnected },
      { icon: 'document', label: 'Reports', route: '/reports', disabled: !isConnected },
      { icon: 'translation', label: 'Translate', route: '/translate', disabled: !isConnected },
      { icon: 'database', label: 'Database', route: '/database/open-database', disabled: isCloudEnv },
      ...(!isCloudEnv
        ? [{ icon: 'a-edit', label: 'Administration', route: '/administration/users', disabled: isCloudEnv }]
        : []),
    ];

    this.navItems = rawNavItems.filter((item) => this.permissionService.canAccessRoute(item.route));

    if (!isCloudEnv && isConnected && (!this.databaseService.connectDatabase.name || !this.databaseService.connectDatabase.server)) {
      this.databaseService
        .getConnectedDatabase()
        .pipe(first())
        .subscribe((db) => {
          if (db) {
            this.databaseService.connectDatabase = { name: db.database, server: db.server };
            this.reloadContent();
          }
        });
    }

    const rawBottomItems = [
      {
        key: 'manuals',
        icon: 'folder',
        label: 'Manuals',
        type: SidepanelTypesV3.ROUTER,
        router: {
          url: '/manuals',
          target: '/manuals',
        },
        route: '/manuals',
      },
      {
        key: 'about',
        icon: 'AboutIcon',
        label: 'About',
        type: SidepanelTypesV3.ROUTER,
        router: {
          url: '/about',
          target: '/about',
        },
        route: '/about',
      },
    ];

    this.bottomItems = rawBottomItems.filter((item) => this.permissionService.canAccessRoute(item.route || ''));
    this.activeSidebarList = this.isBottomNavigationRoute(this.router.url) ? 'bottom' : 'navigation';

    this.reloadContent();
  }

  private isBottomNavigationRoute(url: string): boolean {
    const route = url.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
    return this.bottomItems.some((item) => {
      const itemRoute = String(item.route || '').replace(/\/$/, '') || '/';
      return route === itemRoute || route.startsWith(`${itemRoute}/`);
    });
  }

  private checkToken(isCloudEnv: boolean): void {
    if (isCloudEnv) {
      this.subscription.add(
        this.appInitService.hasAccess$.subscribe((value) => {
          if (value !== null) {
            this.hasAccess = value;
          }
        }),
      );
    } else {
      this.hasAccess = true;
    }
    this.reloadContent();
  }

  private updateSidebarIconPosition() {
    const width = window.innerWidth;
    if (width === this.lastViewportWidth) {
      return;
    }
    this.lastViewportWidth = width;

    if (width >= 768 && width <= 1024) {
      this.slimView = true;
      this.spanded = false;
      this.resizeFeatureContent();
    } else if (width < 768) {
      this.hidden = true;
      this.resizeFeatureContent();
    } else {
      this.slimView = false;
      this.spanded = true;
      this.resizeFeatureContent();
    }
    this.reloadContent();
  }

  private reloadContent(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  public handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasPendingUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private hasPendingUnsavedChanges(): boolean {
    return (
      this.unsavedChangesService.getState() ||
      this.grafcetDataService.getGrafcetChanges() ||
      this.dxTableService.hasPendingChanges()
    );
  }

  public resizeFeatureContent() {
    const elements = document.querySelectorAll<HTMLElement>('.feature-container');
    elements.forEach((el) => {
      el.style.removeProperty('width');
    });

    setTimeout(() => {
      globalThis.dispatchEvent(new Event('resize'));
    }, 0);
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.boundUpdateSidebarIconPosition);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    if (!this.hidden) {
      this.globalClickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const sideMenuContainer = target.closest('[data-sidebar-root]');
        if (!sideMenuContainer) {
          return;
        }
        const sidebarButton = target.closest('.sidebar-toggle-icon');
        if (sidebarButton) {
          this.spanded = !this.spanded;
          this.slimView = !this.spanded;
          this.resizeFeatureContent();
          this.reloadContent();
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.globalClickListener) {
      this.globalClickListener();
    }
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('resize', this.boundUpdateSidebarIconPosition);
    this.subscription.unsubscribe();
  }
}
